// PetMachine - Landing Page (single-file layout)
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Menu, X, Phone, Mail, MapPin, Cpu, Sparkles, Timer, ShieldCheck,
  Play, ChevronRight, Bot, Droplets, PawPrint, Check, LogOut,
  MessageCircle, Instagram, Facebook, Loader2, Store, Building2,
  LayoutDashboard, TrendingUp, Users, Zap, Gauge, ChevronDown, XCircle, Settings2,
} from "lucide-react";
import { API, useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import AuthDialog from "@/components/AuthDialog";
import ReserveDialog from "@/components/ReserveDialog";
import Toast from "@/components/Toast";
import Countdown from "@/components/Countdown";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_conversar-pt-1/artifacts/rge9kz7r_Screenshot_20260627_134600_WhatsAppBusiness.webp";
const PROTOTYPE_FRONT =
  "https://customer-assets-agu9un31.emergentagent.net/job_conversar-pt-1/artifacts/gexsls1n_file_000000003d4c820eafde3d69d2ba3873.png";
const PROTOTYPE_INSIDE =
  "https://customer-assets-agu9un31.emergentagent.net/job_conversar-pt-1/artifacts/3jdorsa3_file_000000004480820e91158f0e33cbd5be.png";
const HERO_BG =
  "https://images.unsplash.com/photo-1614850523011-8f49ffc73908?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjB0ZWNobm9sb2d5JTIwYmFja2dyb3VuZCUyMGJsdWV8ZW58MHx8fHwxNzgzMzUzNzkwfDA&ixlib=rb-4.1.0&q=85";
const ROBOTIC = PROTOTYPE_INSIDE;
const HAPPY_DOG =
  "https://images.unsplash.com/photo-1629030502047-b6ac6d4a78b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxoYXBweSUyMHdldCUyMGRvZyUyMGdyb29taW5nfGVufDB8fHx8MTc4MzM1Mzc5MXww&ixlib=rb-4.1.0&q=85";

const DEFAULT_WHATSAPP = "5547999711358";
const waLink = (num, msg = "") => `https://wa.me/${(num || DEFAULT_WHATSAPP).replace(/\D/g, "")}${msg ? `?text=${encodeURIComponent(msg)}` : ""}`;
const formatPhone = (num) => {
  const d = (num || "").replace(/\D/g, "");
  if (d.length === 13) return `+${d.slice(0,2)} ${d.slice(2,4)} ${d.slice(4,9)}-${d.slice(9)}`;
  if (d.length === 12) return `+${d.slice(0,2)} ${d.slice(2,4)} ${d.slice(4,8)}-${d.slice(8)}`;
  return num || "";
};

const brl = (n) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0 });

function formatErr(detail) {
  if (!detail) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail?.msg || detail);
}

export default function LandingPage() {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reserveModel, setReserveModel] = useState(null);
  const [catalog, setCatalog] = useState({ total_units: 30, sold: 0, remaining: 30, deposit_percent: 20, models: [] });
  const [settings, setSettings] = useState(null);
  const [toast, setToast] = useState(null);

  const loadCatalog = useCallback(async () => {
    try {
      const [{ data: cat }, { data: st }] = await Promise.all([
        axios.get(`${API}/models`),
        axios.get(`${API}/settings`),
      ]);
      setCatalog(cat);
      setSettings(st);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  const requireAuthThen = (fn) => {
    if (!user) {
      setAuthMode("register");
      setAuthOpen(true);
      return;
    }
    fn();
  };

  const openReserve = (model) => {
    requireAuthThen(() => {
      setReserveModel(model);
      setReserveOpen(true);
    });
  };

  const onReserveSuccess = (reservation) => {
    setToast({ kind: "success", msg: "Reserva registrada!" });
    loadCatalog();
    setReserveOpen(false);
  };

  return (
    <div className="min-h-screen text-white" data-testid="landing-page">
      {settings?.promo_enabled && settings.promo_text && (
        <PromoBanner text={settings.promo_text} link={settings.promo_link} />
      )}
      <Header
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        onLogin={() => { setAuthMode("login"); setAuthOpen(true); }}
        onRegister={() => { setAuthMode("register"); setAuthOpen(true); }}
        user={user}
        onLogout={logout}
      />

      <Hero
        remaining={catalog.remaining}
        total={catalog.total_units}
        deadline={settings?.launch_deadline}
        whatsapp={settings?.contact_whatsapp}
        onReserve={() => document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" })}
      />

      <MarqueeBar />

      <PainPoints />

      <HowItWorks />

      <NotRoboticArm />

      <VideoSection />

      <Catalog models={catalog.models} onReserve={openReserve} />

      <SelfServiceSection />

      <TraditionalVsPetMachine />

      <Guarantees remaining={catalog.remaining} total={catalog.total_units} />

      <TargetAudience />

      <NewsletterAndContact onToast={setToast} />

      <Footer settings={settings} />

      <WhatsAppFloating whatsapp={settings?.contact_whatsapp} />

      {authOpen && (
        <AuthDialog
          mode={authMode}
          setMode={setAuthMode}
          onClose={() => setAuthOpen(false)}
          onSuccess={() => setAuthOpen(false)}
        />
      )}
      {reserveOpen && reserveModel && (
        <ReserveDialog
          model={reserveModel}
          onClose={() => setReserveOpen(false)}
          onSuccess={onReserveSuccess}
          onError={(m) => setToast({ kind: "error", msg: m })}
        />
      )}
      {toast && <Toast kind={toast.kind} msg={toast.msg} onDone={() => setToast(null)} />}
    </div>
  );
}

/* ----------------------- HERO IMAGE CAROUSEL --------------------------- */
function HeroImageCarousel() {
  const shots = [
    { src: PROTOTYPE_FRONT, label: "Vista frontal · Fachada" },
    { src: PROTOTYPE_INSIDE, label: "Vista interna · Engenharia" },
  ];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % shots.length), 6000);
    return () => clearInterval(t);
  }, [shots.length]);

  return (
    <div data-testid="hero-carousel">
      <div className="pm-glow-ring pm-card overflow-hidden rounded-3xl pm-float">
        <div className="aspect-[4/3] bg-black relative">
          {shots.map((s, i) => (
            <img
              key={s.src}
              src={s.src}
              alt={s.label}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${idx === i ? "opacity-95" : "opacity-0"}`}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-tr from-[#00f0ff20] via-transparent to-[#0055ff30] mix-blend-screen pointer-events-none" />
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-xs font-mono uppercase tracking-widest">
            <span className="text-[color:var(--pm-neon)]">// {shots[idx].label}</span>
            <span className="text-white/60">Brasil · 2026</span>
          </div>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        {shots.map((s, i) => (
          <button
            key={s.src}
            onClick={() => setIdx(i)}
            className={`group relative border rounded-lg overflow-hidden transition-all ${idx === i ? "border-[color:var(--pm-neon)] shadow-[0_0_18px_-6px_rgba(0,240,255,0.6)]" : "border-white/10 hover:border-white/30"}`}
            data-testid={`hero-carousel-thumb-${i}`}
            aria-label={s.label}
          >
            <img src={s.src} alt="" className="h-14 w-24 object-cover opacity-90" />
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1">
          {shots.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-[color:var(--pm-neon)]" : "w-2 bg-white/25"}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- HEADER -------------------------------- */
function PromoBanner({ text, link }) {
  const inner = (
    <div className="flex items-center justify-center gap-3 text-xs sm:text-sm py-2 px-4 text-center bg-gradient-to-r from-[#00f0ff] via-[#33f5ff] to-[#00f0ff] text-black font-semibold tracking-wide" data-testid="promo-banner">
      <Sparkles size={14} className="shrink-0" />
      <span>{text}</span>
    </div>
  );
  if (link) {
    return <a href={link} target="_blank" rel="noreferrer" className="block">{inner}</a>;
  }
  return inner;
}

function Header({ menuOpen, setMenuOpen, onLogin, onRegister, user, onLogout }) {
  const navigate = useNavigate();
  const links = [
    { href: "#como-funciona", label: "Como Funciona" },
    { href: "#catalogo", label: "Modelos" },
    { href: "#garantias", label: "Garantias" },
    { href: "#publico", label: "Para Quem" },
    { href: "#contato", label: "Contato" },
  ];
  return (
    <header className="pm-glass fixed top-0 inset-x-0 z-50" data-testid="header">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 lg:px-12 h-[72px]">
        <a href="#top" className="flex items-center gap-3" data-testid="logo-link">
          <img src={LOGO_URL} alt="PetMachine" className="h-10 w-auto" />
        </a>
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm text-white/80 hover:text-[color:var(--pm-neon)] transition-colors tracking-wide"
              data-testid={`nav-${l.href.replace("#", "")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              {user.role === "admin" && (
                <button
                  className="pm-btn-ghost text-xs"
                  onClick={() => navigate("/admin")}
                  data-testid="header-admin-button"
                  title="Ir para o painel de controle"
                >
                  <LayoutDashboard size={14} className="inline -mt-1 mr-1" /> Painel
                </button>
              )}
              <span className="text-xs text-white/60 font-mono">{user.email}</span>
              <button className="pm-btn-ghost text-xs" onClick={onLogout} data-testid="header-logout-button">
                <LogOut size={14} className="inline -mt-1 mr-1" /> Sair
              </button>
            </>
          ) : (
            <>
              <button className="pm-btn-ghost text-xs" onClick={onLogin} data-testid="header-login-button">Entrar</button>
              <button className="pm-btn-primary text-xs" onClick={onRegister} data-testid="header-register-button">
                Cadastre-se
              </button>
            </>
          )}
        </div>
        <button
          className="lg:hidden text-white p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-white/5 bg-black/95 backdrop-blur">
          <div className="max-w-7xl mx-auto px-5 py-5 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMenuOpen(false)}
                className="text-white/90 py-1"
                data-testid={`mobile-nav-${l.href.replace("#", "")}`}
              >
                {l.label}
              </a>
            ))}
            <div className="flex gap-2 pt-2 border-t border-white/5">
              {user ? (
                <button className="pm-btn-ghost text-xs w-full" onClick={onLogout} data-testid="mobile-logout-button">
                  <LogOut size={14} className="inline -mt-1 mr-1" /> Sair
                </button>
              ) : (
                <>
                  <button className="pm-btn-ghost text-xs flex-1" onClick={onLogin} data-testid="mobile-login-button">Entrar</button>
                  <button className="pm-btn-primary text-xs flex-1" onClick={onRegister} data-testid="mobile-register-button">Cadastre-se</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

/* --------------------------------- HERO -------------------------------- */
function Hero({ remaining, total, deadline, whatsapp, onReserve }) {
  return (
    <section id="top" className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden">
      <div className="absolute inset-0 pm-grid-bg opacity-60 pointer-events-none" />
      <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-screen pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
        <div className="pm-fade-up">
          <div className="pm-label mb-6">Pré-lançamento oficial · Joinville-SC</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02]">
            O fim do banho manual <br />
            <span className="pm-neon-text">como você conhece.</span>
          </h1>
          <p className="mt-6 text-white/70 text-base sm:text-lg max-w-xl leading-relaxed">
            A <b className="text-white">PetMachine</b> transforma o banho de cães em um processo
            <b className="text-white"> padronizado</b>, <b className="text-white">escalável</b> e
            muito mais <b className="text-white">lucrativo</b>. Desenvolvida no Brasil para pet shops,
            banho & tosa, comodato e tutores exigentes.
          </p>
          <p className="mt-3 text-white/50 text-sm max-w-xl">
            Apenas <b className="text-[color:var(--pm-neon)]">{total} unidades</b> disponíveis no lote inaugural.
          </p>

          {deadline && (
            <div className="mt-8">
              <div className="pm-label text-[10px] mb-3">// Reservas encerram em</div>
              <Countdown deadline={deadline} />
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button className="pm-btn-primary pm-pulse" onClick={onReserve} data-testid="hero-cta-button">
              Reservar Agora <ChevronRight className="inline -mt-0.5" size={16} />
            </button>
            <a
              href={waLink(whatsapp, "Olá! Quero saber mais sobre a PetMachine.")}
              target="_blank"
              rel="noreferrer"
              className="pm-btn-ghost text-xs sm:text-sm flex items-center gap-2 justify-center"
              data-testid="hero-whatsapp-button"
            >
              <MessageCircle size={16} /> Falar no WhatsApp
            </a>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
            <Stat label="Unidades restantes" value={remaining} accent />
            <Stat label="Entrega em" value="6 meses" />
            <Stat label="Melhorias" value="2 anos" />
          </div>
        </div>

        <div className="relative">
          <HeroImageCarousel />
          <div className="absolute -bottom-6 -left-6 hidden sm:block pm-card p-4 backdrop-blur">
            <div className="pm-label">Status</div>
            <div className="font-mono text-sm mt-1">
              <span className="inline-block h-2 w-2 rounded-full bg-[color:var(--pm-neon)] mr-2 animate-pulse" />
              Aceitando reservas
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`pm-card p-4 ${accent ? "pm-glow-ring" : ""}`}>
      <div className="pm-label text-[10px]">{label}</div>
      <div className={`font-mono text-xl mt-1 ${accent ? "pm-neon-text" : "text-white"}`}>{value}</div>
    </div>
  );
}

/* ------------------------------- MARQUEE ------------------------------- */
function MarqueeBar() {
  const items = [
    "PROCESSO PADRONIZADO",
    "ESCALA REAL",
    "IHM + CLP",
    "AUTOATENDIMENTO",
    "COMODATO",
    "MADE IN JOINVILLE",
  ];
  return (
    <div className="border-y border-white/5 py-4 overflow-hidden">
      <div className="flex gap-14 whitespace-nowrap animate-[marquee_28s_linear_infinite] text-xs tracking-[0.35em] text-white/50">
        {[...items, ...items, ...items].map((i, idx) => (
          <span key={idx} className="flex items-center gap-14">
            <span className="text-[color:var(--pm-neon)]">◇</span>{i}
          </span>
        ))}
      </div>
      <style>{`@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
    </div>
  );
}

/* ------------------------------ HOW IT WORKS --------------------------- */
function HowItWorks() {
  const steps = [
    { icon: PawPrint, title: "Pet posicionado", text: "O animal é posicionado com segurança dentro da cabine. Nenhum estresse, nenhuma contenção manual." },
    { icon: Droplets, title: "Ciclo automatizado", text: "O sistema executa as etapas do banho conforme o programa escolhido no painel IHM." },
    { icon: Settings2, title: "Sistemas mecânicos", text: "Escovas, jatos e movimentos automáticos auxiliam a limpeza — variam conforme o modelo escolhido." },
    { icon: Sparkles, title: "Enxágue e drenagem", text: "O ciclo finaliza com enxágue completo e drenagem controlada. Tudo mensurado e repetível." },
  ];
  return (
    <section id="como-funciona" className="py-24 sm:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-14 items-start">
          <div>
            <div className="pm-label mb-4">// Como funciona</div>
            <h2 className="font-display text-3xl sm:text-5xl font-black leading-[1.05]">
              Um ciclo <span className="pm-neon-text">controlado, previsível</span> e repetível.
            </h2>
            <p className="text-white/60 mt-6 leading-relaxed max-w-md">
              Todos os modelos contam com <b className="text-white">IHM + CLP</b>, garantindo controle preciso, repetibilidade e facilidade de operação. Cada ciclo é mensurado — água, produto e tempo.
            </p>
            <div className="mt-8 pm-card p-3 pm-glow-ring overflow-hidden rounded-2xl">
              <img src={PROTOTYPE_INSIDE} alt="Vista interna da PetMachine · sistemas mecânicos automatizados" className="w-full h-56 object-cover rounded-xl opacity-95" />
              <div className="mt-2 text-[11px] text-white/40 font-mono uppercase tracking-widest text-center">
                Vista interna · Cabine automatizada
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {steps.map((s, i) => (
              <div key={s.title} className="pm-card p-6" data-testid={`how-step-${i}`}>
                <div className="flex items-center justify-between">
                  <s.icon className="text-[color:var(--pm-neon)]" size={26} />
                  <span className="font-mono text-xs text-white/40">0{i + 1}</span>
                </div>
                <h3 className="font-display text-xl font-bold mt-4">{s.title}</h3>
                <p className="text-white/60 text-sm mt-2 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- PAIN POINTS ------------------------------ */
function PainPoints() {
  const pains = [
    "Equipe sobrecarregada",
    "Baixa produtividade",
    "Dificuldade em padronizar qualidade",
    "Tempo elevado por atendimento",
  ];
  const gains = [
    { icon: TrendingUp, text: "Aumento de produtividade" },
    { icon: Zap, text: "Redução do esforço físico da equipe" },
    { icon: Check, text: "Padronização do serviço" },
    { icon: Gauge, text: "Controle de consumo de água e produtos" },
    { icon: Cpu, text: "Possibilidade real de escala" },
  ];
  return (
    <section className="py-20 sm:py-28 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="pm-label mb-3">// O problema</div>
            <h2 className="font-display text-3xl sm:text-4xl font-black leading-[1.1]">
              Quem vive do banho conhece <span className="pm-neon-text">esses gargalos.</span>
            </h2>
            <ul className="mt-8 space-y-3">
              {pains.map((p) => (
                <li key={p} className="pm-card p-4 flex items-center gap-3 text-white/85">
                  <XCircle className="text-red-400/80 shrink-0" size={18} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="pm-label mb-3">// Com a PetMachine</div>
            <h2 className="font-display text-3xl sm:text-4xl font-black leading-[1.1]">
              Mais banhos. Menos esforço. <span className="pm-neon-text">Mais resultado.</span>
            </h2>
            <ul className="mt-8 space-y-3">
              {gains.map((g) => (
                <li key={g.text} className="pm-card p-4 flex items-center gap-3 text-white/85">
                  <g.icon className="text-[color:var(--pm-neon)] shrink-0" size={18} />
                  <span>{g.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------ NOT A ROBOTIC ARM ---------------------------- */
function NotRoboticArm() {
  return (
    <section className="py-20 sm:py-24">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="pm-card pm-glow-ring p-8 sm:p-12">
          <div className="pm-label mb-3">// Esclarecimento técnico</div>
          <h2 className="font-display text-2xl sm:text-4xl font-black leading-tight">
            A PetMachine <span className="text-red-400">não</span> utiliza braço robótico.
          </h2>
          <p className="text-white/70 mt-5 leading-relaxed text-base sm:text-lg">
            Ela utiliza <b className="text-white">sistemas mecânicos inteligentes</b>, inspirados em processos industriais,
            que aumentam a eficiência e reduzem o esforço humano — <b className="text-white">mantendo confiabilidade e robustez</b>.
            Automação de verdade, pensada para operação intensiva.
          </p>
        </div>
      </div>
    </section>
  );
}

/* -------------------------- SELF SERVICE ------------------------------- */
function SelfServiceSection() {
  return (
    <section className="py-20 sm:py-28 border-y border-white/5 bg-[#070707]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
        <div>
          <div className="pm-label mb-3">// Muito além do modelo tradicional</div>
          <h2 className="font-display text-3xl sm:text-5xl font-black leading-[1.05]">
            Autoatendimento <br />
            <span className="pm-neon-text">pelo próprio tutor.</span>
          </h2>
          <p className="text-white/70 mt-6 leading-relaxed max-w-xl">
            A PetMachine também permite um novo formato de operação: o próprio cliente
            realiza o banho do seu pet utilizando a máquina.
          </p>
          <div className="mt-8 grid sm:grid-cols-3 gap-3">
            {[
              "Instalação em áreas externas ou estacionamento",
              "Redução total de mão de obra",
              "Nova receita com autoatendimento",
            ].map((t) => (
              <div key={t} className="pm-card p-4 text-sm text-white/80">
                <Check size={14} className="text-[color:var(--pm-neon)] inline mr-2" />
                {t}
              </div>
            ))}
          </div>
        </div>
        <div className="pm-glow-ring pm-card overflow-hidden rounded-3xl">
          <img src={HAPPY_DOG} alt="Autoatendimento pelo tutor" className="w-full h-[380px] object-cover" />
        </div>
      </div>
    </section>
  );
}

/* -------------------- TRADITIONAL VS PETMACHINE ------------------------ */
function TraditionalVsPetMachine() {
  return (
    <section className="py-20 sm:py-28">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 text-center">
        <div className="pm-label mb-3">// A mudança de nível</div>
        <h2 className="font-display text-3xl sm:text-5xl font-black leading-[1.05] max-w-3xl mx-auto">
          De um trabalho artesanal para uma <span className="pm-neon-text">operação escalável.</span>
        </h2>
        <p className="text-white/60 mt-6 max-w-2xl mx-auto text-sm sm:text-base">
          A PetMachine leva sua operação para um modelo baseado em <b className="text-white">processo</b>,
          <b className="text-white"> padronização</b> e <b className="text-white">escala</b>.
          Não substitui pessoas. Potencializa o que sua equipe consegue entregar.
        </p>
        <div className="grid md:grid-cols-3 gap-4 mt-12 text-left">
          {[
            "Padroniza o serviço, independente de quem opera",
            "Reduz o esforço físico e o tempo por animal",
            "Libera a equipe para tarefas de maior valor",
          ].map((t, i) => (
            <div key={t} className="pm-card p-6" data-testid={`change-${i}`}>
              <span className="font-mono text-xs text-white/40">0{i + 1}</span>
              <p className="mt-3 text-white/80 text-sm leading-relaxed">{t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ VIDEO ---------------------------------- */
function VideoSection() {
  return (
    <section className="py-16 sm:py-24" id="video">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="pm-glow-ring pm-card overflow-hidden rounded-3xl">
          <div className="relative aspect-video bg-gradient-to-br from-[#001824] via-black to-[#001430] flex items-center justify-center">
            <div className="absolute inset-0 pm-grid-bg opacity-30" />
            <div className="text-center relative z-10 px-6">
              <button
                className="mx-auto mb-6 w-20 h-20 rounded-full bg-[color:var(--pm-neon)] text-black flex items-center justify-center pm-pulse"
                aria-label="Reproduzir demo"
                data-testid="video-play-button"
              >
                <Play size={28} className="ml-1" fill="currentColor" />
              </button>
              <div className="pm-label">Vídeo demonstrativo</div>
              <h3 className="font-display text-2xl sm:text-3xl font-bold mt-3">
                Veja a PetMachine em ação
              </h3>
              <p className="text-white/60 mt-2 max-w-md mx-auto text-sm">
                Vídeo oficial será divulgado no lançamento. Cadastre seu e-mail para receber em primeira mão.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ CATALOG -------------------------------- */
function Catalog({ models, onReserve }) {
  return (
    <section id="catalogo" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <div className="pm-label mb-3">// Catálogo</div>
            <h2 className="font-display text-3xl sm:text-5xl font-black">
              Escolha o seu <span className="pm-neon-text">modelo.</span>
            </h2>
          </div>
          <p className="max-w-md text-white/60 text-sm">
            Todos os modelos incluem <b className="text-white">2 anos de melhorias</b>, entrega estimada em 6 meses e suporte técnico direto com a fábrica.
          </p>
        </div>

        <div id="catalog" className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {models.map((m) => (
            <div
              key={m.slug}
              className={`pm-card p-6 relative flex flex-col ${m.highlight ? "pm-glow-ring border-[color:var(--pm-border-accent)]" : ""}`}
              data-testid={`model-card-${m.slug}`}
            >
              {m.highlight && (
                <div className="absolute -top-3 left-6 bg-[color:var(--pm-neon)] text-black text-[10px] font-bold px-3 py-1 rounded-full tracking-widest">
                  MAIS ESCOLHIDO
                </div>
              )}
              <div className="pm-label">{m.slug}</div>
              <h3 className="font-display text-2xl font-bold mt-2">{m.name.replace("PetMachine ", "")}</h3>
              <p className="text-white/50 text-xs mt-1">{m.tagline}</p>

              <div className="mt-6 font-mono">
                <div className="text-[10px] text-white/40 uppercase tracking-widest">A partir de</div>
                <div className="text-3xl font-bold mt-1">{brl(m.price)}</div>
              </div>

              <ul className="mt-6 space-y-2.5 flex-1">
                {m.features.map((f) => (
                  <li key={f} className="flex gap-2 text-sm text-white/75">
                    <Check size={16} className="text-[color:var(--pm-neon)] shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                className={`mt-6 ${m.highlight ? "pm-btn-primary" : "pm-btn-ghost"} text-xs w-full`}
                onClick={() => onReserve(m)}
                data-testid={`model-${m.slug}-reserve-button`}
              >
                Reservar {m.name.replace("PetMachine ", "")}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- GUARANTEES ------------------------------- */
function Guarantees({ remaining, total }) {
  const items = [
    { icon: ShieldCheck, title: "2 anos de melhorias", text: "Clientes do lote inicial recebem atualizações e melhorias por 2 anos, mantendo o equipamento sempre evoluído. Condições completas combinadas em contrato no fechamento." },
    { icon: Timer, title: "Entrega estimada: 6 meses", text: "Produção sob demanda a partir da confirmação da reserva. Prazo estimado para as primeiras unidades do lote inaugural." },
    { icon: Cpu, title: `${total} unidades no lote inicial`, text: `Restam apenas ${remaining} unidades no pré-lançamento. Depois, foco em locação e comodato.` },
  ];
  return (
    <section id="garantias" className="py-24 sm:py-32 border-y border-white/5 bg-[#070707]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="pm-label mb-3 text-center">// Pré-lançamento limitado</div>
        <h2 className="font-display text-3xl sm:text-5xl font-black text-center max-w-3xl mx-auto">
          Condições especiais para <span className="pm-neon-text">quem entra agora.</span>
        </h2>

        <div className="grid md:grid-cols-3 gap-5 mt-14">
          {items.map((it, i) => (
            <div key={it.title} className="pm-card p-8" data-testid={`guarantee-${i}`}>
              <it.icon size={32} className="text-[color:var(--pm-neon)]" />
              <h3 className="font-display text-2xl font-bold mt-5">{it.title}</h3>
              <p className="text-white/60 mt-3 text-sm leading-relaxed">{it.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- TARGET AUDIENCE --------------------------- */
function TargetAudience() {
  const groups = [
    { icon: Store, title: "Pet Shops que querem crescer", text: "Aumente ticket médio e produtividade. Automatize o serviço de maior demanda." },
    { icon: Droplets, title: "Banho & Tosa que querem padronizar", text: "Padronize a qualidade, reduza estresse animal e libere sua equipe para o que agrega valor." },
    { icon: Building2, title: "Operações que querem escalar", text: "Modelo baseado em processo e repetibilidade. Cresce sem depender só de mão de obra." },
    { icon: Users, title: "Autoatendimento & Investidores", text: "Instalação em áreas externas, comodato, ou investimento em novos formatos de receita." },
  ];
  return (
    <section id="publico" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-[1fr_1.2fr] gap-14 items-center">
        <div className="pm-glow-ring pm-card overflow-hidden rounded-3xl">
          <img src={HAPPY_DOG} alt="Cão feliz após o banho" className="w-full h-[420px] object-cover" />
        </div>
        <div>
          <div className="pm-label mb-3">// Para quem</div>
          <h2 className="font-display text-3xl sm:text-5xl font-black leading-[1.05]">
            Feita para quem <span className="pm-neon-text">enxerga oportunidade.</span>
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-10">
            {groups.map((g) => (
              <div key={g.title} className="pm-card p-6">
                <g.icon size={22} className="text-[color:var(--pm-neon)]" />
                <h3 className="font-display text-lg font-bold mt-3">{g.title}</h3>
                <p className="text-white/60 text-sm mt-2">{g.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------- NEWSLETTER & CONTACT ---------------------------- */
function NewsletterAndContact({ onToast }) {
  const [nlEmail, setNlEmail] = useState("");
  const [nlLoading, setNlLoading] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
  const [ctLoading, setCtLoading] = useState(false);

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!nlEmail) return;
    setNlLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email: nlEmail });
      setNlEmail("");
      onToast({ kind: "success", msg: "Inscrição confirmada! Você receberá novidades em primeira mão." });
    } catch (err) {
      onToast({ kind: "error", msg: formatErr(err.response?.data?.detail) });
    } finally {
      setNlLoading(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setCtLoading(true);
    try {
      await axios.post(`${API}/contact`, contact);
      setContact({ name: "", email: "", phone: "", message: "" });
      onToast({ kind: "success", msg: "Mensagem enviada! Retornaremos em breve." });
    } catch (err) {
      onToast({ kind: "error", msg: formatErr(err.response?.data?.detail) });
    } finally {
      setCtLoading(false);
    }
  };

  return (
    <section id="contato" className="py-24 sm:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-2 gap-10">
        <div className="pm-card p-8 sm:p-10">
          <div className="pm-label mb-3">// Newsletter</div>
          <h3 className="font-display text-2xl sm:text-3xl font-black">
            Novidades antes de todo mundo.
          </h3>
          <p className="text-white/60 mt-3 text-sm">
            Receba atualizações do desenvolvimento, vídeo demonstrativo e datas do lançamento oficial.
          </p>
          <form onSubmit={submitNewsletter} className="mt-6 flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              required
              placeholder="seu@email.com"
              className="pm-input flex-1"
              value={nlEmail}
              onChange={(e) => setNlEmail(e.target.value)}
              data-testid="newsletter-email-input"
            />
            <button className="pm-btn-primary text-xs" disabled={nlLoading} data-testid="newsletter-submit-button">
              {nlLoading ? <Loader2 size={14} className="animate-spin inline" /> : "Inscrever"}
            </button>
          </form>
        </div>

        <div className="pm-card p-8 sm:p-10">
          <div className="pm-label mb-3">// Fale com a fábrica</div>
          <h3 className="font-display text-2xl sm:text-3xl font-black">Vamos conversar sobre a sua operação.</h3>
          <form onSubmit={submitContact} className="mt-6 space-y-3">
            <input
              required minLength={2}
              className="pm-input"
              placeholder="Seu nome"
              value={contact.name}
              onChange={(e) => setContact({ ...contact, name: e.target.value })}
              data-testid="contact-form-name"
            />
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                type="email" required
                className="pm-input"
                placeholder="E-mail"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                data-testid="contact-form-email"
              />
              <input
                className="pm-input"
                placeholder="Telefone / WhatsApp"
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                data-testid="contact-form-phone"
              />
            </div>
            <textarea
              required minLength={5}
              className="pm-input min-h-[110px] resize-y"
              placeholder="Conte um pouco sobre o que você precisa..."
              value={contact.message}
              onChange={(e) => setContact({ ...contact, message: e.target.value })}
              data-testid="contact-form-message"
            />
            <button className="pm-btn-primary text-xs w-full sm:w-auto" disabled={ctLoading} data-testid="contact-form-submit">
              {ctLoading ? <Loader2 size={14} className="animate-spin inline" /> : "Enviar mensagem"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- FOOTER -------------------------------- */
function Footer({ settings }) {
  const s = settings || {};
  return (
    <footer className="border-t border-white/5 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-14 grid md:grid-cols-4 gap-10 text-sm">
        <div className="md:col-span-2">
          <img src={LOGO_URL} alt="PetMachine" className="h-14 w-auto" />
          <p className="text-white/50 mt-4 max-w-sm leading-relaxed">
            A primeira cabine robotizada de banho para cães fabricada no Brasil. Feita em Joinville-SC.
          </p>
        </div>
        <div>
          <div className="pm-label mb-4">Contato</div>
          <ul className="space-y-3 text-white/70">
            <li className="flex gap-2 items-center"><Phone size={14} className="text-[color:var(--pm-neon)]" /> {formatPhone(s.contact_whatsapp) || "+55 47 99971-1358"}</li>
            <li className="flex gap-2 items-center"><Mail size={14} className="text-[color:var(--pm-neon)]" /> {s.contact_email || "contato@petmachine.ind.br"}</li>
            {s.contact_email_sales && (
              <li className="flex gap-2 items-center"><Mail size={14} className="text-[color:var(--pm-neon)]" /> {s.contact_email_sales}</li>
            )}
            <li className="flex gap-2 items-center"><MapPin size={14} className="text-[color:var(--pm-neon)]" /> {s.contact_city || "Joinville · SC · Brasil"}</li>
          </ul>
        </div>
        <div>
          <div className="pm-label mb-4">Redes</div>
          <ul className="space-y-3 text-white/70">
            {s.instagram && <li className="flex gap-2 items-center"><Instagram size={14} /> {s.instagram}</li>}
            {s.facebook && <li className="flex gap-2 items-center"><Facebook size={14} /> {s.facebook}</li>}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/5 text-center py-6 text-xs text-white/40 font-mono">
        © {new Date().getFullYear()} PETMACHINE.IND.BR · Feita em Joinville com engenharia de precisão.
      </div>
    </footer>
  );
}

/* -------------------------- WHATSAPP FLOAT ----------------------------- */
function WhatsAppFloating({ whatsapp }) {
  return (
    <a
      href={waLink(whatsapp, "Olá! Cheguei pelo site da PetMachine e quero saber mais.")}
      target="_blank"
      rel="noreferrer"
      className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-[#25D366] text-white font-bold pl-4 pr-5 py-3 rounded-full shadow-[0_0_30px_-6px_rgba(37,211,102,0.7)] hover:scale-[1.03] transition-transform pm-pulse"
      data-testid="whatsapp-floating-button"
    >
      <MessageCircle size={20} /> WhatsApp
    </a>
  );
}
